import { useState } from 'react';
import { api, ApiError, fmtDate, fmtDay, idemKey, useApi } from '../api';
import type { DashboardLayout, DayBar, Kpi } from '../data/types';
import { Icon, KpiCard, KpiGrid, Meter, PageHeader, ReachBars, Seg, Tag, tabular } from '../ui';

interface DashboardModel {
  loop: { step: string; name: string; count: string; desc: string; next: string }[];
  recommendations: { id: string; type: string; surface: string; title: string; why: string; evidenceConfidence: string; expect: string }[];
  relationships: { name: string; rho: number | null; state: string; n: number; evidenceConfidence: number }[];
  relationshipsNote: string;
  latestLearning: { title: string; state: string; n: number; at: string | null } | null;
  signals: { id: string; title: string; kind: string; surface: string; severity: string; state: string; when: string | null; paid: boolean }[];
  kpis: Kpi[];
  days: { day: string; organicReach24h: number; paidImpressions: number }[];
}
interface Portfolio { brands: { id: string; name: string; status: string; connectedAccounts: number; posts7d: number; openAlerts: number; openRecommendations: number; supportedLearnings: number; connection: string | null }[] }

/** Organic early reach vs paid impressions on one scale (both are people/impressions counts; labelled as such). */
export const toBars = (days: { day: string; organicReach24h: number; paidImpressions: number }[]): DayBar[] => {
  const max = Math.max(1, ...days.map((d) => d.organicReach24h + d.paidImpressions));
  return days.map((d) => ({ org: (d.organicReach24h / max) * 100, paid: (d.paidImpressions / max) * 100, label: fmtDay(d.day) }));
};
const sevCls = (s: string) => (s === 'CRITICAL' ? 'tag-accent' : s === 'WARNING' ? 'tag-outline' : 'tag-neutral');

interface Props { brandId: string; brand: string; layout: DashboardLayout; setLayout: (l: DashboardLayout) => void; onBrand: (id: string) => void; goStudio: () => void; goPlans: () => void; onDecided: () => void }

export function Dashboard({ brandId, brand, layout, setLayout, onBrand, goStudio, goPlans, onDecided }: Props) {
  const [feedFilter, setFeedFilter] = useState<'all' | 'organic' | 'paid'>('all');
  const { data, error, reload } = useApi<DashboardModel>(`/api/v1/brands/${brandId}/dashboard`);
  const portfolio = useApi<Portfolio>(layout === 'portfolio' ? '/api/v1/workspace/portfolio' : null);
  const [decided, setDecided] = useState<Record<string, string>>({});
  const decide = async (id: string, decision: 'ACCEPT' | 'DISMISS') => {
    try {
      const r = await api<{ status: string }>('POST', `/api/v1/recommendations/${id}/decision`, { decision, idempotencyKey: idemKey(), ...(decision === 'DISMISS' ? { reasonCode: 'NOT_RELEVANT' } : {}) });
      setDecided((x) => ({ ...x, [id]: r.status === 'ACCEPTED' ? 'Accepted · draft plan created' : 'Dismissed' }));
      onDecided();
    } catch (e) { setDecided((x) => ({ ...x, [id]: e instanceof ApiError ? e.message : 'Failed' })); }
  };
  if (error) return <p className="text-muted">Dashboard unavailable: {error.message}</p>;
  if (!data) return <p className="text-muted">Loading…</p>;
  const open = data.recommendations.filter((r) => !decided[r.id]).length;
  return (
    <>
      <PageHeader kicker={`${brand} · last 28 days`} title="Dashboard">
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <span className="text-muted" style={{ fontSize: 12 }}>Layout</span>
          <Seg name="dl" value={layout} onChange={setLayout} options={[{ value: 'loop', label: 'Loop' }, { value: 'signals', label: 'Signals' }, { value: 'portfolio', label: 'Portfolio' }]} />
        </div>
      </PageHeader>

      {layout === 'loop' && (
        <>
          <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--space-3)' }}>
            {data.loop.map((st) => (
              <div key={st.step} className="card elev-sm" style={{ gap: 'var(--space-2)', position: 'relative' }}>
                <div className="card-kicker">{st.step} · {st.name}</div>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: 30, lineHeight: 1 }}>{st.count}</div>
                <p className="card-body">{st.desc}</p>
                <div className="card-meta"><Icon name="ph-arrow-right" style={{ color: 'var(--color-accent)' }} /><span>{st.next}</span></div>
              </div>
            ))}
          </section>
          <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-6)', alignItems: 'start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}><h4 style={{ margin: 0 }}>Open recommendations</h4><span className="text-muted" style={{ fontSize: 12 }}>{open} awaiting a decision</span></div>
              {data.recommendations.length === 0 && <div className="card elev-sm"><p className="card-body">No open recommendations. They appear once a learning is observed or supported on this brand's own data.</p></div>}
              {data.recommendations.map((r) => {
                const st = decided[r.id];
                return (
                  <div key={r.id} className="card elev-sm" style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 'var(--space-4)', opacity: st ? 0.6 : 1 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}><Tag cls="tag-outline">{r.surface}</Tag><Tag cls="tag-neutral">{r.type === 'ACT' ? 'Act' : r.type === 'TEST' ? 'Test' : 'Review'}</Tag><span className="text-muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>evidence {r.evidenceConfidence}</span></div>
                      <div className="card-title" style={{ fontSize: 15 }}>{r.title}</div>
                      <p className="card-body">{r.why}</p>
                      <div className="card-meta"><Icon name="ph-target" /><span>Expected: {r.expect}</span></div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 'none' }}>
                      {!st && (<><button className="btn btn-primary" onClick={() => decide(r.id, 'ACCEPT')}><Icon name="ph-check" />Act</button><button className="btn btn-secondary" onClick={() => decide(r.id, 'DISMISS')}>Dismiss</button></>)}
                      {st && <><Tag cls="tag-accent">{st}</Tag>{st.startsWith('Accepted') && <button className="btn btn-ghost" onClick={goPlans}>Open Plans<Icon name="ph-arrow-right" /></button>}</>}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}><h4 style={{ margin: 0 }}>Observed relationships</h4><span className="text-muted" style={{ fontSize: 12 }}>on {brand}'s own posts</span></div>
              <div className="card elev-sm" style={{ gap: 'var(--space-3)' }}>
                <p className="card-body" style={{ flex: 'none' }}>{data.relationshipsNote}</p>
                {data.relationships.map((w) => (
                  <div key={w.name} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 44px', gap: '4px 10px', alignItems: 'center', fontSize: 13 }}>
                    <span>{w.name} <span className="text-muted" style={{ fontSize: 11 }}>· {w.state.toLowerCase()} · n {w.n}</span></span>
                    <span style={{ textAlign: 'right', color: 'var(--color-accent-300)', ...tabular }}>{w.rho === null ? '—' : w.rho.toFixed(2)}</span>
                    <div style={{ gridColumn: '1 / -1' }}><Meter pct={Math.abs(w.rho ?? 0) * 100} /></div>
                  </div>
                ))}
                <div className="card-meta"><Icon name="ph-info" /><span>Associations, not causes · evidence confidence per relationship in Analytics</span></div>
              </div>
              <div className="card elev-sm">
                <div className="card-kicker">Latest learning</div>
                {data.latestLearning ? (<><div className="card-title" style={{ fontSize: 15 }}>{data.latestLearning.title}</div><div className="card-meta"><span>{data.latestLearning.state.toLowerCase()} · n {data.latestLearning.n} · {fmtDate(data.latestLearning.at)}</span></div></>)
                  : <p className="card-body">No learning yet.</p>}
              </div>
            </div>
          </section>
        </>
      )}

      {layout === 'signals' && (
        <section style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', flex: '1 1 520px', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <h4 style={{ margin: 0 }}>Signal feed</h4><span className="text-muted" style={{ fontSize: 12 }}>open alerts and the last 24 h</span>
              <Seg name="sf" style={{ marginLeft: 'auto' }} value={feedFilter} onChange={setFeedFilter} options={[{ value: 'all', label: 'All' }, { value: 'organic', label: 'Organic' }, { value: 'paid', label: 'Paid' }]} />
            </div>
            <div style={{ overflowX: 'auto', minWidth: 0 }}>
              <table className="table" style={{ minWidth: 560 }}>
                <thead><tr><th>Signal</th><th>Surface</th><th>Severity</th><th>Status</th><th>Detected</th><th /></tr></thead>
                <tbody>
                  {data.signals.filter((s) => feedFilter === 'all' || (feedFilter === 'paid') === s.paid).map((s) => (
                    <tr key={s.id}>
                      <td><div style={{ display: 'flex', flexDirection: 'column' }}><span>{s.title}</span><span className="text-muted" style={{ fontSize: 12 }}>{s.kind}</span></div></td>
                      <td><Tag cls="tag-outline">{s.surface}</Tag></td>
                      <td><Tag cls={sevCls(s.severity)}>{s.severity.toLowerCase()}</Tag></td>
                      <td className="text-muted">{s.state.toLowerCase()}</td>
                      <td className="text-muted">{fmtDate(s.when)}</td>
                      <td style={{ textAlign: 'right' }}><button className="btn btn-ghost" onClick={goStudio}>Discuss<Icon name="ph-arrow-right" /></button></td>
                    </tr>
                  ))}
                  {data.signals.length === 0 && <tr><td colSpan={6} className="text-muted">No alerts. Monitors run every 15 minutes to daily.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', flex: '0 1 200px', marginLeft: 'auto' }}>
            {data.kpis.map((k) => <KpiCard key={k.label} k={k} />)}
          </div>
        </section>
      )}

      {layout === 'portfolio' && (
        <>
          <KpiGrid kpis={data.kpis} />
          <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-6)', alignItems: 'start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <h4 style={{ margin: 0 }}>Brands</h4>
              <table className="table">
                <thead><tr><th>Brand</th><th>Accounts</th><th>Posts 7d</th><th>Open alerts</th><th>Open recs</th><th>Learnings</th></tr></thead>
                <tbody>
                  {(portfolio.data?.brands ?? []).map((t) => (
                    <tr key={t.id} className="clickable-row" onClick={() => onBrand(t.id)}>
                      <td style={{ color: t.id === brandId ? 'var(--color-accent)' : 'var(--color-text)' }}>{t.name}{t.status !== 'ACTIVE' && <span className="text-muted" style={{ fontSize: 11 }}> · {t.status.toLowerCase()}</span>}</td>
                      <td style={tabular}>{t.connectedAccounts}</td>
                      <td style={tabular}>{t.posts7d}</td>
                      <td style={tabular}>{t.openAlerts}</td>
                      <td style={tabular}>{t.openRecommendations}</td>
                      <td style={tabular}>{t.supportedLearnings}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-muted" style={{ fontSize: 12 }}>Each brand's observations, learnings and connections are kept separate; only brands you have a role on are listed.</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <h4 style={{ margin: 0 }}>Reach by day · organic vs paid</h4>
              <div className="card elev-sm" style={{ gap: 'var(--space-3)' }}>
                <ReachBars days={toBars(data.days)} height={160} />
                <div className="card-meta"><span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--color-accent-500)' }} /><span>Organic · 24 h reach of posts published that day</span><span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--color-accent-800)', marginLeft: 10 }} /><span>Paid · impressions (Meta-reported)</span><span style={{ marginLeft: 'auto' }}>14 days</span></div>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
}
